import { NavigationCollectionType, ContentPositionTimelineUnit } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { defineStore } from "pinia";
import {
  CirrusReaderPaginationType,
  CirrusReaderSyntheticPaginationSegmentationMethod
} from "../shared/CirrusReaderModel";
import type { IIntegerRange } from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import type { ILocator } from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import type { 
  ICirrusReaderSyntheticPageData, 
  ICirrusReaderReadingProgressionTimelineData, 
  ICirrusReaderReadingProgressionTimeline, 
  ICirrusReaderNavigationItem, 
  ICirrusReaderSyntheticPageListData, 
  ICirrusReaderNavigationCollection 
} from "../shared/CirrusReaderModel";

interface CirrusPageListState {
  activeSyntheticPage: ICirrusReaderSyntheticPageData | undefined;
  timelineData: ICirrusReaderReadingProgressionTimelineData | undefined;
  pageSizeInWordCount: number;
  cirrusReaderProgressionTimeline:
    | ICirrusReaderReadingProgressionTimeline
    | undefined;
  syntheticPages: ICirrusReaderSyntheticPageData[];
  pageList: ICirrusReaderSyntheticPageData[];
}
const _cirrusReaderNavigationTree = useCirrusNavigationTree();
export const useCirrusPageListStore = defineStore("cirrusPageList", {
  state: (): CirrusPageListState => ({
    activeSyntheticPage: undefined,
    timelineData: undefined,
    pageSizeInWordCount: 500,
    cirrusReaderProgressionTimeline: undefined,
    syntheticPages: [],
    pageList: [],
  }),
  actions: {
    initialisePageList(
      cirrusReaderProgressionTimeline: ICirrusReaderReadingProgressionTimeline,
      pageSizeInWordCount: number = 500
    ) {
      this.pageSizeInWordCount = pageSizeInWordCount;
      this.cirrusReaderProgressionTimeline = cirrusReaderProgressionTimeline;
    },
    async buildPageList() {
      if (!this.timelineData && this.cirrusReaderProgressionTimeline) {
        this.timelineData =
          await this.cirrusReaderProgressionTimeline.serialize();
        if (!this.timelineData) {
          throw new Error(
            "CirrusReaderPublicationPageList.build(), Unable to serialize CirrusReaderProgressionTimeline instance."
          );
        }
      }

      const hasPageList = _cirrusReaderNavigationTree.treeData.collections.find(
        (collection) => collection.type === NavigationCollectionType.PAGE_LIST
      );
      if (!hasPageList) {
        this.pageList = await this.buildSyntheticPageListFromTimelineData();
      } else {
        this.pageList = await this.buildSyntheticPageListFromNavTreeData();
      }

      const pageList: ICirrusReaderSyntheticPageListData = this.serialize();
      this.setSyntheticPages(pageList);
      return pageList;
    },
    async buildSyntheticPageListFromTimelineData() {
      const pageList: ICirrusReaderSyntheticPageData[] = [];
      if (this.timelineData && this.timelineData.sections) {
        let currentPageIndex: number = 0;
        for (const section of this.timelineData.sections) {
          const currentSectionStartPosition: number = section.range.start;
          let currentSectionEndPosition: number = section.range.end;
          let nextPageStartPosition: number = currentSectionStartPosition;

          // If it's a fixed layout publication, such as a PDF, the range will have the same start and end index.
          // In the while loop below we expect an "end exclusive" range however, so we need to increment the
          // end by one index.
          if (
            this.timelineData.unit === ContentPositionTimelineUnit.PAGES &&
            currentSectionStartPosition === currentSectionEndPosition
          ) {
            currentSectionEndPosition++;
          }

          while (nextPageStartPosition < currentSectionEndPosition) {
            const nextPageEndPosition: number =
              nextPageStartPosition + this.pageSizeInWordCount >= currentSectionEndPosition
                ? currentSectionEndPosition
                : nextPageStartPosition + this.pageSizeInWordCount;
            const navItem: ICirrusReaderNavigationItem | undefined =
              this.getNearestNavItemFromNavCollection(
                nextPageStartPosition,
                NavigationCollectionType.TOC
              );
            const navItems: ICirrusReaderNavigationItem[] | undefined = navItem
              ? [navItem]
              : undefined;
            const locator: string | undefined = (
              await this.cirrusReaderProgressionTimeline?.fetchContentLocationAsRange(
                nextPageStartPosition,
                nextPageEndPosition
              )
            )
              ?.getLocator()
              .toString();

            pageList.push({
              label: (currentPageIndex + 1).toString(),
              index: currentPageIndex,
              navItems: navItems,
              range: new IntRange(nextPageStartPosition, nextPageEndPosition),
              locatorData: locator,
              section: new IntRange(section.range.start, section.range.end),
            });

            currentPageIndex++;
            nextPageStartPosition = nextPageEndPosition;
          }
        }
      }
      return pageList;
    },
    async buildSyntheticPageListFromNavTreeData(){
        const pages: ICirrusReaderSyntheticPageData[] | undefined = [];
        let currentPageIndex = 0;
        const pageListCollection = _cirrusReaderNavigationTree.treeData.collections.find((item: ICirrusReaderNavigationCollection) => {
            return item.type === NavigationCollectionType.PAGE_LIST;
        });

        if(!pageListCollection) {
            console.warn('CirrusReaderPublicationPageList._buildSyntheticPageListFromNavTreeData(), navigation tree data has no page-list collection');
            return [];
        }

        for(const page of pageListCollection.children) {

            if(page.timelineStartPosition !== undefined) {

                const nextPageIndex = Math.min(currentPageIndex + 1, pageListCollection.children.length - 1);
                const child = pageListCollection.children[nextPageIndex];
                const nextPageStartPosition = child ? child.timelineStartPosition : page.timelineStartPosition + 1;

                if(nextPageStartPosition && nextPageStartPosition >= page.timelineStartPosition) {
                    const range = new IntRange(page.timelineStartPosition, nextPageStartPosition);
                    const integerRange: IIntegerRange | undefined = this.getSectionFromTimelinePosition(page.timelineStartPosition)?.range;
                    const section = integerRange ? new IntRange(integerRange.start, integerRange.end) : undefined;
                    const navItem = this.getNearestNavItemFromNavCollection(page.timelineStartPosition, NavigationCollectionType.TOC);
                    const navItems: ICirrusReaderNavigationItem[] | undefined = navItem ? [navItem] : undefined;
                    const locator =  (await this.cirrusReaderProgressionTimeline?.fetchContentLocationAsRange(page.timelineStartPosition, nextPageStartPosition))?.getLocator().toString();
                    pages.push(
                        {
                            label: page.title,
                            index: currentPageIndex,
                            range,
                            section,
                            locatorData: locator,
                            navItems
                        }
                    );
                }
            } else {
                console.warn(`CirrusReaderPublicationPageList._buildSyntheticPageListFromNavTreeData, Page has no start position`, page);
            }
            currentPageIndex++;
        }
        return pages;
    },
    getNearestNavItemFromNavCollection(
        position: number, 
        collectionType: NavigationCollectionType
    ){
        if (_cirrusReaderNavigationTree.treeData) {
            const tree = _cirrusReaderNavigationTree.treeData;

            if (!tree) {
                console.warn('CirrusReaderPublicationPageList._getNearestNavItemFromNavCollection(), No navigation tree data.')
                return undefined;
            }

            const collection = tree.collections.find(collection => collection.type === collectionType);

            if (!collection) {
                console.warn('CirrusReaderPublicationPageList._getNearestNavItemFromNavCollection(), Publication has no TOC.')
                return undefined;
            }

            let currentPos = 0;
            let foundItem: ICirrusReaderNavigationItem | undefined = undefined;

            while (currentPos < collection.children.length) {
              const currentItem = collection.children[currentPos];
              if (currentItem){
                if(position === currentItem.timelineStartPosition) {
                    foundItem = currentItem;
                    break;
                }

                if (currentItem.timelineStartPosition === undefined || position > currentItem.timelineStartPosition) {
                    currentPos++;
                } else {
                    // If the position is greater than the current items position we return the previous nav item,
                    // otherwise we return the current position.
                    const itemIndex = currentItem.timelineStartPosition > position 
                      ? currentPos - 1
                      : currentPos;
                    foundItem = collection.children[itemIndex];
                    break;
                }
              }
            }
            return foundItem;
        }
        return undefined;
    },
    setSyntheticPages(pageList: ICirrusReaderSyntheticPageListData){
        this.syntheticPages = pageList.pages;
        if (this.timelineData) {
            const _sliderStepSize = Math.floor(this.timelineData.length / this.getSyntheticPageCount());
            useCirrusReaderNavigation().setSliderStepSize(_sliderStepSize);
        }
        if (useCirrusReaderNavigation().pageNumber == 0){
          useCirrusReaderNavigation().setSliderProgressionValue(0);
        }
    },
    getSectionFromTimelinePosition(position: number){
        if (!this.timelineData || !this.timelineData.sections) {
            console.warn('CirrusReaderPublicationPageList._getSectionFromTimelinePosition(), Timeline data does not exist, or has no sections data.');
            return;
        }
        return this.timelineData.sections.find(section => position >= section.range.start && position < section.range.end);
    },
    paginationType() {
        let returnValue: CirrusReaderPaginationType;

        const pageList = _cirrusReaderNavigationTree.treeData?.collections.find(collection => collection.type === NavigationCollectionType.PAGE_LIST);

        switch (this.timelineData?.unit) {
            case ContentPositionTimelineUnit.PAGES:
                returnValue = CirrusReaderPaginationType.SECTIONS;
                break;
            case ContentPositionTimelineUnit.DOCUMENTS:
                returnValue = CirrusReaderPaginationType.SECTIONS;
                break;
            default:
                returnValue = pageList ? CirrusReaderPaginationType.PAGE_LIST : CirrusReaderPaginationType.SYNTHETIC;
        }
        return returnValue;
    },
    getSyntheticPageCount(){
        let returnValue: number;

        switch (this.timelineData?.unit) {
            case ContentPositionTimelineUnit.PAGES:
                returnValue = this.timelineData.sections?.length || 0;
                break;
            case ContentPositionTimelineUnit.DOCUMENTS:
                returnValue = this.timelineData.sections?.length || 0;
                break;
            default:
                returnValue = this.syntheticPages.length;
        }
        return returnValue;
    },
    async fetchPagesForLocator(locator: ILocator) {
      try {
        const timelineRange = await this.cirrusReaderProgressionTimeline?.fetchTimelineRange(locator);
        if (!timelineRange) {
            return;
        }
        const startIndex = timelineRange.start;
        let endIndex = timelineRange.end;
        if(timelineRange.start === timelineRange.end) {
            endIndex = endIndex + 1;
        }

        return this.pageList.filter(page => {
            return page.range.intersects(new IntRange(startIndex, endIndex));
        });
      } catch (err) {
        console.warn(`Unable to fetchPagesForLocator()`, err);
      }
    },
    serialize(){
      const syntheticPaginationSegmentationMethod: CirrusReaderSyntheticPaginationSegmentationMethod | undefined =
          this.paginationType() === CirrusReaderPaginationType.SYNTHETIC ?
          CirrusReaderSyntheticPaginationSegmentationMethod.UNICODE_WHITESPACE_SEGMENTATION : undefined;

      return {
          pages: this.pageList,
          paginationType: this.paginationType(),
          syntheticPaginationSegmentationMethod
      };
    }
  }
});
